import { action, makeObservable, observable } from 'mobx';
import { AuthenticateByCodeStore } from './authenticateByCode';
import { RootStore } from './root';
import {
  loginTwoFactorAuthenticationSendCodeByPhoneClick,
  loginTwoFactorAuthenticationSendCodeByEmailClickSrc6Evid175,
  loginTwoFactorAuthenticationLoginWithAuthenticatorAppClick,
  loginClickOnBack,
} from '@wix/bi-logger-hls2/v2';
import { loginTwoFactorAuthenticationLoginWithOwnerAppClick } from '@wix/bi-logger-identity-data/v2';
import {
  TwoFactorAuthMethod,
  TwoFactorAuthMethods,
  TwoFactorAuthPhoneDeliveryMethod,
} from '../utils/constants';
import { ApprovalChallengeStatus } from './premiumUsers2FaEnforcement/ownerApp/types';
import { extractErrorKeyByErrorCode } from '../utils/errorHandler';

export class EnterCodeStore {
  private readonly rootStore: RootStore;

  rememberMe: boolean = true;
  isSwitchingMethod: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      rememberMe: observable,
      isSwitchingMethod: observable,
      onSwitchMethod: action,
      onToggleRememberThisDevice: action,
    });
  }

  onToggleRememberThisDevice() {
    this.rememberMe = !this.rememberMe;
  }

  async onSwitchMethod(
    newMethod: TwoFactorAuthMethod,
    authStore: AuthenticateByCodeStore,
    options: {
      phoneDeliveryMethod?: TwoFactorAuthPhoneDeliveryMethod;
      suppressToast?: boolean;
    } = {},
  ) {
    const { phoneDeliveryMethod, suppressToast } = options;
    // Prevent multiple simultaneous switch attempts
    if (this.isSwitchingMethod) {
      return;
    }

    const origin = authStore.currentTwoFAMethod;
    // Catalog swap: "ByPhone" is the Email event, "ByEmail" (evid 175) the Phone
    // event. Only Phone/175 carries delivery_method (SMS / WhatsApp / voice).
    const biEvent =
      newMethod === TwoFactorAuthMethods.Phone
        ? loginTwoFactorAuthenticationSendCodeByEmailClickSrc6Evid175({
            origin,
            delivery_method: phoneDeliveryMethod,
          })
        : {
            [TwoFactorAuthMethods.Email]:
              loginTwoFactorAuthenticationSendCodeByPhoneClick,
            [TwoFactorAuthMethods.TOTP]:
              loginTwoFactorAuthenticationLoginWithAuthenticatorAppClick,
            [TwoFactorAuthMethods.Push]:
              loginTwoFactorAuthenticationLoginWithOwnerAppClick,
          }[newMethod]?.({ origin });

    biEvent && this.rootStore.biLogger.report(biEvent);

    // Clear any previous notification and set loading state
    this.rootStore.notificationStore.close();
    this.isSwitchingMethod = true;

    try {
      if (authStore.switchToMethod) {
        // Atomic switch - API call first, then state update on success
        await authStore.switchToMethod(newMethod, { phoneDeliveryMethod });
      } else {
        // Fallback for stores that don't support atomic switch
        authStore.currentTwoFAMethod = newMethod;
        await authStore.resendCode({ phoneDeliveryMethod });
      }

      // Switch succeeded - clear the code field for the new method
      authStore.clear();

      // Handle Push method special case
      if (newMethod === TwoFactorAuthMethods.Push) {
        this.rootStore.twoFactorAuthStore.setTwoFactorAuthStatus(
          ApprovalChallengeStatus.PENDING_APPROVAL,
        );
      }
    } catch (error) {
      // Switch failed - stay on current method. Legacy callers show a floating
      // toast; the Lobby suppresses it and renders the error inline under the
      // clicked button, using the returned error code.
      const errorCode = error instanceof Error ? error.message : String(error);
      if (!suppressToast) {
        const errorKey = extractErrorKeyByErrorCode(errorCode);
        if (errorKey) {
          this.rootStore.notificationStore.openErrorToast(
            this.rootStore.i18n.t(errorKey),
            3000,
          );
        }
      }
      return errorCode;
    } finally {
      this.isSwitchingMethod = false;
    }
  }

  onBackButtonClicked(method: string) {
    this.rootStore.biLogger.report(
      loginClickOnBack({
        step_name: 'verify_code',
        flow_type: method,
        // delivery_method only applies to the Phone factor; empty otherwise.
        delivery_method:
          method === TwoFactorAuthMethods.Phone
            ? this.rootStore.twoFactorAuthStore.phoneDeliveryMethod
            : undefined,
      }),
    );
    this.rootStore.navigationStore.goBack();
  }
}
